<script setup lang="ts">

import { type EcommerceItem } from '@/types/shared/player-config'
import { computed } from 'vue'
import type { MysteryBoxItem } from '@/types/mystery-box'
import StarsBalancePill from '@/components/StarsBalancePill.vue'
import { useShop } from '@/composables/game/shop'
import DefaultBadge from '@/components/periodic/DefaultBadge.vue'
import type { BadgeVariant } from '@/components/periodic/DefaultBadge.vue'
import PaymentButtons from '@/components/PaymentButtons.vue'
import { useEcommercePrice } from '@/composables/game/ecommerce'

const props = defineProps<{
	boxItem: MysteryBoxItem
	ecommerceItem: EcommerceItem
}>()

const emit = defineEmits<{
	(e: 'onPurchase'): void
}>()

const { getStarPrice } = useEcommercePrice()
const { isDefaultBOGO, isBOGO } = useShop()

const starPrice = getStarPrice(props.ecommerceItem)

const itemMultiplier = computed(() => {
  if (props.ecommerceItem.id.endsWith('X1')) return 1
  if (props.ecommerceItem.id.endsWith('X3')) return 3
  if (props.ecommerceItem.id.endsWith('X5')) return 5
  return 1
})

const hasBOGO = computed(() => isBOGO.value)
const hasDefaultBOGO = computed(() => hasBOGO.value && isDefaultBOGO.value)

const saleVariant = computed<BadgeVariant>(() => {
  if (isDefaultBOGO.value) return 'BOGO'
  return 'discount' // fallback
})

function handlePurchase(paid: boolean) {
  if (paid) {
    emit('onPurchase')
  }
}

</script>

<template>
  <StarsBalancePill class="absolute top-[50px] left-0" />
  <div class="relative">
    <img
      class="size-[160px] object-cover"
      :src="boxItem.image"
    >
    <DefaultBadge
      v-if="hasDefaultBOGO"
      :variant="saleVariant"
      class="absolute left-4 top-4 w-[55px] h-auto z-[3]"
    />
  </div>
  <div class="mt-6 text-light-white text-[24px] leading-8 font-bold -tracking-[0.48px]">
    {{ boxItem.name }}<span>{{ ' ' }}</span><span class="lowercase">x{{
      itemMultiplier * (hasBOGO ? 2 : 1)
    }} = ${{ starPrice?.usdAmount }}</span>
  </div>
  <div class="mt-12 w-full">
    <PaymentButtons
      :item="ecommerceItem"
      @purchase="({ paid }) => handlePurchase(paid)"
    />
  </div>
</template>

<style scoped>

</style>